import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Prakriya, FormButton, Variant } from "../components/declension/prakriya";
import {
    CASE_ORDER,
    NUM_ORDER,
    DerivedForm,
    Paradigm,
    declineParadigm,
} from "./declension";
import { StemAnalysis } from "./vidyut-engine";
import { CaseName, Gender, Num } from "./types";

/**
 * A declension table derived on the spot, with each form clickable to show its
 * derivation.
 *
 * Used by the study pages for pronouns, numerals and participles. They are all
 * ordinary declension as far as the engine is concerned — a pronoun's special
 * endings fall out of its being *sarvanāman* (1.1.27), not out of anything the
 * caller has to say — so the only difference between them is which stems to
 * show.
 */
export const ParadigmTable = (props: {
    analysis: StemAnalysis;
    gender: Gender;
    /** Shown above the table. */
    caption?: React.ReactNode;
    /**
     * Which numbers to show. Defaults to all three. Numerals need this: the
     * engine will happily derive a nominative singular of *dvi*, but the word is
     * a dual and that form does not exist.
     */
    numbers?: Num[];
    /**
     * Which cases to show. Defaults to all eight. The personal pronouns need
     * this: the engine will derive a vocative of *asmad*, and there is no such
     * thing — one does not address oneself.
     */
    cases?: CaseName[];
}) => {
    const [paradigm, setParadigm] = useState<Paradigm | null>(null);
    const [selected, setSelected] = useState<DerivedForm | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const shown: Num[] = props.numbers ?? NUM_ORDER;
    const rows: CaseName[] = props.cases ?? CASE_ORDER;

    useEffect(() => {
        let cancelled = false;
        setParadigm(null);
        setSelected(undefined);
        setFailed(false);
        declineParadigm(props.analysis, props.gender)
            .then(p => { if (!cancelled) setParadigm(p); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
        // The analysis is a plain object rebuilt on each render, so compare by value.
    }, [JSON.stringify(props.analysis), props.gender]);

    return (
        <Block>
            {props.caption && <Caption>{props.caption}</Caption>}

            {failed && <Note>could not be derived</Note>}
            {!failed && !paradigm && <Note>…</Note>}

            {paradigm && (
                <>
                    <Table>
                        <tbody>
                            <tr>
                                <THead />
                                {shown.map(n => (
                                    <THead key={n}>
                                        {n === "s" ? "sg." : n === "d" ? "du." : "pl."}
                                    </THead>
                                ))}
                            </tr>
                            {rows.map((caseName: CaseName) => (
                                <tr key={caseName}>
                                    <THead className="case">{caseName}</THead>
                                    {shown.map((num: Num) => {
                                        const forms = paradigm.cells[caseName][num];
                                        if (forms.length === 0) return <td key={num}>&mdash;</td>;
                                        return (
                                            <td key={num}>
                                                {forms.map((f, i) => (
                                                    <React.Fragment key={i}>
                                                        {i > 0 && <Variant> / </Variant>}
                                                        <FormButton
                                                            type="button"
                                                            data-selected={selected === f}
                                                            onClick={() => setSelected(f)}
                                                            title="Show derivation"
                                                        >
                                                            {f.text}
                                                        </FormButton>
                                                    </React.Fragment>
                                                ))}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {selected && <Prakriya form={selected} onClose={() => setSelected(undefined)} />}
                </>
            )}
        </Block>
    );
};

const Block = styled.div`
    flex: 1 1 260px;
    min-width: 240px;
`;

const Caption = styled.div`
    text-align: center;
    font-weight: bold;
    background-color: ${(props) => props.theme.colors.pink};
    font-size: 0.9em;
    padding: 2px;
`;

const Note = styled.div`
    text-align: center;
    opacity: 0.5;
    padding: 12px;
`;

const Table = styled.table`
    font-size: 0.75em;
    width: 100%;
    margin: 6px 0 14px 0;

    td { padding: 2px 4px; white-space: nowrap; }
`;

const THead = styled.td`
    font-weight: bold;
    text-transform: capitalize;
`;

/** Lays paradigms out side by side and wraps them. */
export const ParadigmGrid = styled.div`
    display: flex;
    flex-flow: row wrap;
    gap: 14px;
    align-items: flex-start;
`;

export const GroupHeading = styled.h3`
    width: 100%;
    margin: 18px 0 4px 0;
    font-size: 1em;
`;
